/**
 * Aba "Conversas" do detalhe do ticket (v12): o atendente vê e responde os
 * chats (WhatsApp/widget) do cliente SEM sair do ticket.
 *
 * Duas listas, sempre separadas visualmente:
 *  - VINCULADAS a este ticket (GET /chats?ticket_number=N)
 *  - OUTRAS do mesmo cliente (GET /chats?client_id=X menos as já vinculadas),
 *    cada uma com "Vincular a este ticket" (a mesma mutation PUT /chats/:id
 *    que o LinkTicketDialog usa).
 *
 * Nada aqui reimplementa a conversa: a thread (ChatThread) e o composer
 * (ChatComposer, com mídia/modelo HSM/janela de 24h) vêm de features/chats.
 * As duas listas usam `chatsListOptions`, então caem sob o mesmo prefixo de
 * key que o dispatcher realtime já invalida em chat.updated.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link2 } from 'lucide-react';
import type { ChatListItem, TicketDetail } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { useAuthUser } from '@/auth/use-auth';
import { cn } from '@/lib/utils';
import { useRealtimeConnected } from '@/lib/realtime';
import { chatDetailOptions, chatsListOptions, useChatMessages } from '@/features/chats/chats-queries';
import { useAssumeChat, useUpdateChat } from '@/features/chats/chats-mutations';
import { ChatComposer, ChatThread, isChatGone } from '@/features/chats/chat-window';
import { StartWhatsappAction } from './start-whatsapp-action';
import {
  ChatAvatar,
  ExternalBotBadge,
  OriginBadge,
  StatusBadge,
  errorMessage,
  formatRelativeTime,
} from '@/features/chats/chats-format';

export function ConversasTab({ ticket }: { ticket: TicketDetail }) {
  const ticketNumber = ticket.ticketNumber;
  const clientId = ticket.client.id;

  // SSE conectado ⇒ chat.updated invalida a família da lista; o polling vira
  // fallback lento (mesma política da lista do /chats).
  const realtimeUp = useRealtimeConnected();
  const pollMs = realtimeUp ? 60_000 : 8_000;

  // tab 'all': aqui interessa o HISTÓRICO — conversas encerradas incluídas.
  const linkedQ = useQuery({
    ...chatsListOptions({ tab: 'all', ticketNumber }),
    refetchInterval: pollMs,
  });
  const clientQ = useQuery({
    ...chatsListOptions({ tab: 'all', clientId }),
    refetchInterval: pollMs,
  });

  const linked = useMemo(() => linkedQ.data ?? [], [linkedQ.data]);
  // "Outras do cliente" = as do cliente MENOS as já vinculadas a este ticket
  // (o item da lista não carrega ticketNumber; a subtração por id resolve sem
  // engordar o contrato). Conversas vinculadas de OUTRO cliente continuam
  // aparecendo em "vinculadas", que é o que o atendente espera.
  const others = useMemo(() => {
    const linkedIds = new Set(linked.map((c) => c.id));
    return (clientQ.data ?? []).filter((c) => !linkedIds.has(c.id));
  }, [clientQ.data, linked]);

  // Seleção: a escolha do atendente; sem escolha, a primeira conversa
  // vinculada ao ticket (o caso comum ao abrir a aba).
  const [picked, setPicked] = useState<number | null>(null);
  const selectedId = picked ?? linked[0]?.id ?? null;

  const isPending = linkedQ.isPending || clientQ.isPending;
  const isError = linkedQ.isError || clientQ.isError;
  const error = linkedQ.error ?? clientQ.error;

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <div className="flex max-h-[36rem] flex-col overflow-hidden rounded-lg border bg-card shadow-sm">
        <div className="shrink-0 border-b px-3 py-2.5">
          <StartWhatsappAction ticket={ticket} onOpened={setPicked} onExisting={setPicked} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isPending ? (
            <div aria-busy className="space-y-3 p-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Skeleton className="size-8 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-2/3" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <EmptyState message={`Erro ao carregar conversas: ${errorMessage(error)}`} />
          ) : linked.length === 0 && others.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Nenhuma conversa por aqui ainda</p>
              <p className="mt-1.5">
                Aparecem aqui as conversas de WhatsApp e do widget vinculadas a este ticket, e as
                demais conversas de <span className="font-medium">{ticket.client.name}</span>. Você
                pode vincular qualquer uma delas a este ticket ou iniciar uma conversa nova no
                WhatsApp.
              </p>
            </div>
          ) : (
            <>
              <ChatGroup
                title="Vinculadas a este ticket"
                chats={linked}
                selectedId={selectedId}
                onSelect={setPicked}
                emptyHint="Nenhuma conversa vinculada — vincule uma das conversas do cliente abaixo."
              />
              <ChatGroup
                title={`Outras conversas de ${ticket.client.name}`}
                chats={others}
                selectedId={selectedId}
                onSelect={setPicked}
                ticketNumber={ticketNumber}
                emptyHint="Nenhuma outra conversa deste cliente."
              />
            </>
          )}
        </div>
      </div>

      {selectedId === null ? (
        <div className="flex h-[36rem] items-center justify-center rounded-lg border bg-card shadow-sm">
          <EmptyState message="Selecione uma conversa para ler e responder aqui." />
        </div>
      ) : (
        <div className="flex h-[36rem] min-w-0 flex-col overflow-hidden rounded-lg border bg-card shadow-sm">
          <TicketChatConversation key={selectedId} chatId={selectedId} />
        </div>
      )}
    </div>
  );
}

// ── Listas ──────────────────────────────────────────────────────────────────

function ChatGroup({
  title,
  chats,
  selectedId,
  onSelect,
  ticketNumber,
  emptyHint,
}: {
  title: string;
  chats: ChatListItem[];
  selectedId: number | null;
  onSelect: (chatId: number) => void;
  /** presente ⇒ cada linha ganha "Vincular a este ticket" */
  ticketNumber?: number;
  emptyHint: string;
}) {
  return (
    <section>
      <h3 className="sticky top-0 z-1 truncate border-b bg-muted/60 px-3 py-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase backdrop-blur-xs">
        {title}
      </h3>
      {chats.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">{emptyHint}</p>
      ) : (
        <ul className="divide-y divide-divider">
          {chats.map((chat) => (
            <li key={chat.id}>
              <ChatRow
                chat={chat}
                active={chat.id === selectedId}
                onSelect={() => onSelect(chat.id)}
                {...(ticketNumber !== undefined ? { ticketNumber } : {})}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ChatRow({
  chat,
  active,
  onSelect,
  ticketNumber,
}: {
  chat: ChatListItem;
  active: boolean;
  onSelect: () => void;
  ticketNumber?: number;
}) {
  return (
    <div className={cn('transition-colors', active ? 'bg-accent' : 'hover:bg-accent/50')}>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active || undefined}
        className="flex w-full items-start gap-2.5 px-3 pt-2.5 pb-1.5 text-left"
      >
        <ChatAvatar name={chat.requestorName} className="mt-0.5" />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-semibold">{chat.requestorName}</span>
            <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
              {formatRelativeTime(chat.lastMessageAt ?? chat.createdAt)}
            </span>
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {chat.lastMessagePreview ?? 'Sem mensagens'}
            </span>
            {chat.unreadCount > 0 ? (
              <span
                aria-label={`${chat.unreadCount} mensagens não lidas`}
                className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-destructive px-1 text-[10px] leading-none font-semibold text-white"
              >
                {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
              </span>
            ) : null}
          </span>
          <span className="mt-1 flex items-center gap-1.5">
            <OriginBadge origin={chat.origin} short className="px-1 py-0 text-[10px]" />
            <StatusBadge
              status={chat.status}
              canceled={chat.canceled}
              className="px-1 py-0 text-[10px]"
            />
            {/* spec 021: o bot externo ainda conduz esta conversa */}
            {chat.externalBotActive ? <ExternalBotBadge className="px-1" /> : null}
          </span>
        </span>
      </button>
      {ticketNumber !== undefined ? (
        <div className="px-3 pb-2 pl-[3.125rem]">
          <LinkToTicketButton chatId={chat.id} ticketNumber={ticketNumber} />
        </div>
      ) : null}
    </div>
  );
}

/** PUT /chats/:id { ticketNumber } — a mesma mutation do LinkTicketDialog. */
function LinkToTicketButton({ chatId, ticketNumber }: { chatId: number; ticketNumber: number }) {
  const update = useUpdateChat(chatId);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-6 px-2 text-[11px]"
        disabled={update.isPending}
        onClick={() => update.mutate({ ticketNumber })}
      >
        {update.isPending ? <Spinner className="size-3" /> : <Link2 className="size-3" />}
        Vincular a este ticket
      </Button>
      {update.isError ? (
        <p className="mt-1 text-[11px] text-destructive" role="alert">
          {errorMessage(update.error)}
        </p>
      ) : null}
    </>
  );
}

// ── Conversa embutida ───────────────────────────────────────────────────────

/**
 * Mesma conversa do /chats, com um header enxuto (sem transferir/finalizar/
 * painel de info — isso segue no /chats). Thread e composer são os componentes
 * de features/chats: mídia, modelo HSM e janela de 24h valem igual aqui.
 */
function TicketChatConversation({ chatId }: { chatId: number }) {
  const me = useAuthUser();
  const realtimeUp = useRealtimeConnected();
  const assume = useAssumeChat();
  const detailQ = useQuery({
    ...chatDetailOptions(chatId),
    refetchInterval: realtimeUp ? 60_000 : 8_000,
    retry: (failureCount, e) => !isChatGone(e) && failureCount < 3,
  });
  // Em paralelo com o detalhe (como no /chats) — o resync de ticks usa origin.
  const messagesQ = useChatMessages(chatId, detailQ.data?.origin);

  const chat = detailQ.data;

  if (detailQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (!chat) {
    // 404 = a conversa saiu da visibilidade deste usuário (transferida para um
    // departamento que ele não enxerga): volta ao estado vazio, sem erro duro.
    if (isChatGone(detailQ.error)) {
      return (
        <EmptyState
          message="Esta conversa não está mais disponível para você."
          className="h-full"
        />
      );
    }
    return (
      <EmptyState
        message={`Erro ao carregar a conversa: ${errorMessage(detailQ.error)}`}
        className="h-full"
      />
    );
  }

  const isMine = chat.responsible != null && me != null && chat.responsible.id === me.id;

  return (
    <>
      <div className="flex shrink-0 items-center gap-2.5 border-b px-4 py-2.5">
        <ChatAvatar name={chat.requestorName} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold">{chat.requestorName}</span>
            <OriginBadge origin={chat.origin} className="px-1.5 py-0 text-[10px]" />
            <StatusBadge
              status={chat.status}
              canceled={chat.canceled}
              className="px-1.5 py-0 text-[10px]"
            />
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {[
              chat.client?.name,
              chat.waContact ?? chat.requestorPhone,
              chat.desk?.name,
              chat.ticketNumber !== null ? `Ticket #${chat.ticketNumber}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        {chat.status === 'inbox' ? (
          <Button size="sm" onClick={() => assume.mutate(chat.id)} disabled={assume.isPending}>
            {assume.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Assumir conversa
          </Button>
        ) : null}
      </div>

      <ChatThread
        chatId={chatId}
        messages={messagesQ.data}
        isPending={messagesQ.isPending}
        whatsapp={chat.origin === 'whatsapp'}
      />
      <ChatComposer chat={chat} isMine={isMine} />
    </>
  );
}
